/**
 * 自动连线算法类
 * 提供多种自动连线模式
 * 
 * 连线模式：
 * - sequential: 顺序连接（1→2→3→4）
 * - insert: 插入模式（新节点连接到最后一个节点）
 * - nearest: 最近邻模式（新节点连接到距离最近的节点）
 */

class AutoConnector {
  /**
   * 顺序连接模式
   * 按节点列表顺序连接：1→2→3→4
   * @param {Array<Node>} nodes - 节点数组
   * @returns {Array<Edge>} 生成的连线数组
   */
  sequentialConnect(nodes) {
    if (!nodes || nodes.length < 2) {
      return []
    }

    const edges = []
    
    for (let i = 0; i < nodes.length - 1; i++) {
      const fromNode = nodes[i]
      const toNode = nodes[i + 1]
      
      const distance = this.calculateDistance(
        fromNode.latitude || fromNode.lat,
        fromNode.longitude || fromNode.lon || fromNode.lng,
        toNode.latitude || toNode.lat,
        toNode.longitude || toNode.lon || toNode.lng
      )
      
      edges.push({
        fromNodeId: fromNode.id,
        toNodeId: toNode.id,
        bidirectional: 1,  // 双向
        weight: distance
      })
    }
    
    return edges
  }

  /**
   * 插入模式
   * 将新节点连接到最后一个节点
   * @param {Array<Node>} existingNodes - 现有节点数组
   * @param {Node} newNode - 新节点
   * @returns {Edge|null} 生成的连线
   */
  insertConnect(existingNodes, newNode) {
    if (!existingNodes || existingNodes.length === 0 || !newNode) {
      return null
    }
    
    const lastNode = existingNodes[existingNodes.length - 1]
    
    const distance = this.calculateDistance(
      lastNode.latitude || lastNode.lat,
      lastNode.longitude || lastNode.lon || lastNode.lng,
      newNode.latitude || newNode.lat,
      newNode.longitude || newNode.lon || newNode.lng
    )
    
    return {
      fromNodeId: lastNode.id,
      toNodeId: newNode.id,
      bidirectional: 1,
      weight: distance
    }
  }

  /**
   * 最近邻模式
   * 将新节点连接到距离最近的节点
   * @param {Array<Node>} existingNodes - 现有节点数组
   * @param {Node} newNode - 新节点
   * @returns {Edge|null} 生成的连线
   */
  nearestNeighborConnect(existingNodes, newNode) {
    if (!existingNodes || existingNodes.length === 0 || !newNode) {
      return null
    }
    
    const nearestNode = this.findNearestNode(existingNodes, newNode)
    
    if (!nearestNode) {
      return null
    }
    
    const distance = this.calculateDistance(
      nearestNode.latitude || nearestNode.lat,
      nearestNode.longitude || nearestNode.lon || nearestNode.lng,
      newNode.latitude || newNode.lat,
      newNode.longitude || newNode.lon || newNode.lng
    )
    
    return {
      fromNodeId: nearestNode.id,
      toNodeId: newNode.id,
      bidirectional: 1,
      weight: distance
    }
  }

  /**
   * 计算两点之间的距离（米）
   * 使用Haversine公式
   * @param {number} lat1 - 纬度1
   * @param {number} lon1 - 经度1
   * @param {number} lat2 - 纬度2
   * @param {number} lon2 - 经度2
   * @returns {number} 距离（米）
   */
  calculateDistance(lat1, lon1, lat2, lon2) {
    const R = 6371000 // 地球半径（米）
    const dLat = this.toRadians(lat2 - lat1)
    const dLon = this.toRadians(lon2 - lon1)
    
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRadians(lat1)) * Math.cos(this.toRadians(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2)
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    const distance = R * c
    
    return Math.round(distance * 100) / 100 // 保留2位小数
  }

  /**
   * 查找最近的节点
   * @param {Array<Node>} nodes - 节点数组
   * @param {Node} targetNode - 目标节点
   * @returns {Node|null} 最近的节点
   */
  findNearestNode(nodes, targetNode) {
    if (!nodes || nodes.length === 0 || !targetNode) {
      return null
    }
    
    let nearestNode = null
    let minDistance = Infinity
    
    nodes.forEach(node => {
      const distance = this.calculateDistance(
        node.latitude || node.lat,
        node.longitude || node.lon || node.lng,
        targetNode.latitude || targetNode.lat,
        targetNode.longitude || targetNode.lon || targetNode.lng
      )
      
      if (distance < minDistance) {
        minDistance = distance
        nearestNode = node
      }
    })
    
    return nearestNode
  }

  /**
   * 角度转弧度
   * @param {number} degrees - 角度
   * @returns {number} 弧度
   */
  toRadians(degrees) {
    return degrees * Math.PI / 180
  }

  /**
   * 根据连线模式生成连线
   * @param {string} mode - 连线模式：'sequential' | 'insert' | 'nearest'
   * @param {Array<Node>} existingNodes - 现有节点数组
   * @param {Node} newNode - 新节点（可选，用于insert和nearest模式）
   * @returns {Array<Edge>|Edge|null} 生成的连线
   */
  connect(mode, existingNodes, newNode = null) {
    switch (mode) {
      case 'sequential':
        return this.sequentialConnect(existingNodes)
      case 'insert':
        return this.insertConnect(existingNodes, newNode)
      case 'nearest':
        return this.nearestNeighborConnect(existingNodes, newNode)
      default:
        console.warn(`未知的连线模式: ${mode}`)
        return null
    }
  }
}

// 导出单例
export default new AutoConnector()
